import clsx from 'clsx';
import type { JSX, MouseEvent } from 'react';
import { Children, forwardRef, useCallback } from 'react';

type CustomButtonProps = {
  variant?: 'default' | 'empty' | 'ghost' | 'link' | 'icon';
  size?: 'default' | 'small';
  active?: boolean;
};
type ButtonProps = Resolve<JSX.IntrinsicElements['button'] & CustomButtonProps>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    onClick,
    children,
    className = '',
    disabled = false,
    variant = 'default',
    size = 'default',
    active = false,
    ...props
  },
  ref,
) {
  const countChildren = Children.count(children);
  const isEmpty = countChildren === 0;

  // Handler: external onClick callback
  const handleOnClick = useCallback(
    (evt: MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      if (disabled) return;
      onClick?.(evt);
    },
    [onClick, disabled],
  );

  return (
    <button
      className={clsx(
        'ml--button',
        `ml--button-${variant}`,
        active && `ml--button--active`,
        'appearance-none outline-none overflow-hidden inline-flex m-0 text-black font-sans leading-none rounded items-center overflow-hidden select-none transition whitespace-nowrap',
        !disabled && active ? 'border-black' : 'border-silver',

        variant === 'default' && 'border border-solid py-2 px-3 bg-white',
        variant === 'icon' && 'border border-solid bg-white',

        variant === 'ghost' && 'border-none text-left underline-offset-4 hover:underline bg-transparent',
        variant === 'link' &&
          'border-none text-left underline underline-offset-4 text-metal hover:text-black bg-transparent',

        !disabled && 'hover:border-black cursor-pointer',
        isEmpty && 'border-dashed w-20',
        {
          'h-8': size === 'default',
          'h-6': size === 'small',
          'text-sm': size === 'default',
          'text-xs': size === 'small',
          'w-8': variant === 'icon' && size === 'default',
          'w-6': variant === 'icon' && size === 'small',
        },
        className,
      )}
      onClick={handleOnClick}
      {...props}
      ref={ref}
    >
      {variant === 'icon' ? (
        <div className="w-full h-full flex items-center justify-center">{children as React.ReactNode}</div>
      ) : (
        (children as React.ReactNode)
      )}
      {isEmpty && '\u00A0'}
    </button>
  );
});
